import React from 'react';
import styled from 'styled-components';
import { MordenCard } from '../../atoms/modern-card/modern-card';
import { ApplePayBtn } from '../../atoms/apple-pay-button/apple-pay-button.aom';
import { GooglePayBtn } from '../../atoms/google-pay-button/google-pay-button.aom';
import { Trans, useTranslation } from 'react-i18next';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { getConfig } from '../../../common/utils/get-config';
import Apple from '../../../common/assets/Apple.png';
import AmazonPaySvg from '../../../assets/Amazon-Pay--Streamline-Font-Awesome.svg';


const ExpressCardContent = styled(MordenCard)`
  margin-top: 8px;
  .legend {
    text-align: center;
    color: var(--muted);
    font-weight: 600;
    margin-bottom: 12px;
  }
  .cta {
    display: grid;
    gap: 10px;
  }
`;

const OrDivider = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  color: var(--muted);
  margin: 8px 0 0 0;
  &::before,
  &::after {
    content: '';
    height: 1px;
    background: var(--border);
    display: block;
  }
`;

type Props = {
  applePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  googlePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  amazonPay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  paypal?: {
    available: boolean;
    loading: boolean;
    currency: string;
    createOrder?: () => Promise<string>;
    createSubscription?: () => Promise<string>;
    onApprove: (data: { orderID: string; subscriptionID?: string }) => Promise<void>;
    onCancel: () => void;
    isSubscription?: boolean;
  };
};

const PayPalExpressButton: React.FC<{
  paypal: NonNullable<Props['paypal']>;
}> = ({ paypal }) => {
  const clientId = getConfig('REACT_APP_PAYPAL_CLIENT_ID');

  if (!clientId) return null;

  const isSubscription = !!paypal.isSubscription;

  const scriptOptions: Record<string, any> = {
    clientId,
    components: 'buttons',
    currency: paypal.currency || 'USD',
    intent: isSubscription ? 'subscription' : 'capture',
  };
  if (isSubscription) {
    scriptOptions.vault = 'true';
  }

  const buttonProps: Record<string, any> = {
    style: {
      layout: 'horizontal',
      color: 'gold',
      shape: 'rect',
      label: isSubscription ? 'subscribe' : 'paypal',
      height: 44,
      tagline: false,
    },
    onApprove: paypal.onApprove,
    onCancel: paypal.onCancel,
    onError: (err: any) => {
      console.error('[PayPalExpress] SDK error:', err);
      paypal.onCancel();
    },
    disabled: paypal.loading,
  };

  if (isSubscription && paypal.createSubscription) {
    buttonProps.createSubscription = paypal.createSubscription;
  } else if (paypal.createOrder) {
    buttonProps.createOrder = paypal.createOrder;
  }

  const providerKey = `${isSubscription ? 'sub' : 'order'}-${paypal.currency || 'USD'}`;

  return (
    <PayPalButtonWrapper>
      <PayPalScriptProvider key={providerKey} options={scriptOptions as any}>
        <PayPalButtons {...buttonProps} />
      </PayPalScriptProvider>
    </PayPalButtonWrapper>
  );
};

export const ExpressCard: React.FC<Props> = ({
  applePay,
  googlePay,
  amazonPay,
  paypal,
}) => {
  const { t } = useTranslation();
  return (
    <ExpressCardContent>
      <div className="legend">{t('expressCheckout')}</div>
      <div className="cta">
        {paypal?.available && <PayPalExpressButton paypal={paypal} />}
        {applePay?.available && (
          <ApplePayBtn
            onClick={applePay?.onClick}
            disabled={!applePay?.available || applePay?.loading}
          >
            {applePay?.loading ? (
              ''
            ) : (
              <Trans
                i18nKey="payWith"
                values={{ method: 'Apple Pay' }}
                components={{
                  method: <></>,
                }}
              />
            )}
            <img src={Apple} alt="Apple" style={{ width: 18, height: 18 }} />
          </ApplePayBtn>
        )}
        {googlePay?.available && (
          <GooglePayBtn
            onClick={googlePay?.onClick}
            disabled={!googlePay?.available || googlePay?.loading}
          >
            {googlePay?.loading ? (
              ''
            ) : (
              <Trans
                i18nKey="payWith"
                values={{ method: 'Google Pay' }}
                components={{
                  method: <></>,
                }}
              />
            )}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          </GooglePayBtn>
        )}
        {amazonPay?.available && (
          <AmazonPayBtn
            type="button"
            onClick={amazonPay?.onClick}
            disabled={!amazonPay?.available || amazonPay?.loading}
          >
            {amazonPay?.loading ? (
              ''
            ) : (
              <Trans
                i18nKey="payWith"
                values={{ method: 'Amazon Pay' }}
                components={{
                  method: <></>,
                }}
              />
            )}
            <img src={AmazonPaySvg} alt="Amazon Pay" width="24" height="24" />
          </AmazonPayBtn>
        )}
      </div>
      <OrDivider>{t('or')}</OrDivider>
    </ExpressCardContent>
  );
};

const PayPalButtonWrapper = styled.div`
  width: 100%;
  min-height: 44px;
`;

const AmazonPayBtn = styled.button`
  width: 100%;
  border: none;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  background: #f59e0b;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
